import { ArrowRight, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { ToofiResumeTarget } from "@/types/student-toofi";

const reasonLabel: Record<NonNullable<ToofiResumeTarget["sourceReason"]>, string> = {
  guided_lesson: "Step-by-step lesson",
  unfinished_essay: "Writing in progress",
  due_review: "Review flow",
  plan_context: "Active plan",
  recent_resource: "Resource thread",
  recent_thread: "Recent thread",
};

export function ToofiResumeCard({
  target,
  onResume,
  compact = false,
}: {
  target: ToofiResumeTarget;
  onResume: () => void;
  compact?: boolean;
}) {
  return (
    <div
      className={cn(
        "rounded-[1.5rem] border border-[var(--athr-border-soft)] bg-[linear-gradient(180deg,rgba(255,252,245,0.96),rgba(248,250,252,0.92))] shadow-[0_14px_30px_rgba(15,23,42,0.07)]",
        compact ? "p-3.5" : "p-4 sm:p-5",
      )}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[1rem] border border-[var(--athr-border-soft)] bg-white text-amber-700 shadow-[0_8px_20px_rgba(148,163,184,0.10)]">
          <PlayCircle className="h-4.5 w-4.5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold text-[var(--athr-text-secondary)]">Resume</p>
          <h3 className={cn("mt-1 font-semibold tracking-tight text-[var(--athr-text-primary)]", compact ? "text-sm" : "text-base")}>
            {target.label}
          </h3>
          {target.sourceReason ? (
            <p className="mt-1 text-[11px] font-medium text-[var(--athr-text-secondary)]">{reasonLabel[target.sourceReason]}</p>
          ) : null}
          {target.subtitle ? (
            <p className={cn("mt-1 line-clamp-2 text-[var(--athr-text-secondary)]", compact ? "text-xs leading-5" : "text-sm leading-6")}>
              {target.subtitle}
            </p>
          ) : null}
        </div>
      </div>
      <Button
        type="button"
        variant="secondary"
        className={cn(
          "mt-4 rounded-full",
          compact ? "h-9 px-3.5 text-xs font-semibold" : "h-10 px-4 text-sm",
        )}
        onClick={onResume}
      >
        {target.actionLabel}
        <ArrowRight className="ms-2 h-4 w-4" />
      </Button>
    </div>
  );
}
